/**
 * PlatformMockup — renders a realistic Instagram / Facebook / TikTok /
 * LinkedIn UI shell around the post media so clients see exactly how the
 * post will look on the target platform.
 *
 * Inputs:
 *   - platform: "instagram" | "facebook" | "tiktok" | "linkedin"
 *   - imageUrl: src of the (possibly brand-kit-overlaid) post image
 *   - caption: caption string
 *   - hashtags: optional array of "#tag" strings
 *   - company: { name, logo_url, brand_color, instagram_account, facebook_page }
 */
import { useMemo } from "react";
import {
  Heart, MessageCircle, Send, Bookmark, MoreHorizontal, ThumbsUp, Share2,
  Globe, Music, Plus, Repeat2, Volume2,
} from "lucide-react";

const PLATFORM_RATIO = {
  instagram: "aspect-square",
  facebook:  "aspect-[1.91/1]",
  tiktok:    "aspect-[9/16]",
  linkedin:  "aspect-[1.91/1]",
};

const initialsOf = (name = "?") =>
  name.split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();

const cleanHandle = (s) => (s || "").replace(/^@/, "").trim();

function Avatar({ size = 36, company, displayName }) {
  if (company?.logo_url) {
    return (
      <img src={company.logo_url} alt="" className="rounded-full object-cover shrink-0"
           style={{ width: size, height: size }} />
    );
  }
  return (
    <div
      className="rounded-full flex items-center justify-center text-white font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.35, background: company?.brand_color || "#2563EB" }}
    >
      {initialsOf(displayName)}
    </div>
  );
}

export default function PlatformMockup({ platform, imageUrl, caption, hashtags = [], company = {} }) {
  const platformKey = ["instagram", "facebook", "tiktok", "linkedin"].includes(platform) ? platform : "instagram";

  const handle = useMemo(() => {
    if (platformKey === "instagram") return cleanHandle(company.instagram_account) || cleanHandle(company.name).toLowerCase().replace(/\s+/g, "_");
    if (platformKey === "facebook")  return cleanHandle(company.facebook_page) || company.name || "Brand";
    if (platformKey === "tiktok")    return cleanHandle(company.instagram_account) || cleanHandle(company.name).toLowerCase().replace(/\s+/g, "");
    return company.name || "Brand";
  }, [platformKey, company]);

  const displayName = company.name || handle;
  const tagsText = (hashtags || []).join(" ");
  const wrapperBase = "rounded-xl overflow-hidden border shadow-sm max-w-sm";

  if (platformKey === "instagram") {
    return (
      <div className={`${wrapperBase} bg-white border-neutral-200`} data-testid="mockup-instagram">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2.5">
          <div className="flex items-center gap-2.5">
            <div className="p-[2px] rounded-full" style={{ background: "linear-gradient(45deg,#feda75,#fa7e1e,#d62976,#962fbf,#4f5bd5)" }}>
              <div className="bg-white p-[2px] rounded-full"><Avatar size={32} company={company} displayName={displayName} /></div>
            </div>
            <div className="leading-tight">
              <p className="text-[13px] font-semibold text-neutral-900">{handle}</p>
              <p className="text-[10px] text-neutral-500">Gesponsert</p>
            </div>
          </div>
          <MoreHorizontal className="w-5 h-5 text-neutral-700" />
        </div>
        {/* Image */}
        <div className={`${PLATFORM_RATIO.instagram} bg-neutral-100 flex items-center justify-center`}>
          {imageUrl ? <img src={imageUrl} alt="" className="w-full h-full object-cover" /> :
            <span className="text-xs text-neutral-400">Kein Bild</span>}
        </div>
        {/* Actions */}
        <div className="px-3 pt-2.5 flex items-center justify-between">
          <div className="flex items-center gap-3.5 text-neutral-800">
            <Heart className="w-6 h-6" />
            <MessageCircle className="w-6 h-6" />
            <Send className="w-6 h-6" />
          </div>
          <Bookmark className="w-6 h-6 text-neutral-800" />
        </div>
        {/* Likes + Caption */}
        <div className="px-3 pt-1.5 pb-3">
          <p className="text-[13px] font-semibold text-neutral-900">1&apos;247 Gefaellt mir-Angaben</p>
          {(caption || tagsText) && (
            <p className="text-[13px] text-neutral-900 mt-1 leading-snug">
              <span className="font-semibold mr-1">{handle}</span>
              <span className="whitespace-pre-wrap">{caption}</span>
              {tagsText && <span className="text-[#3897F0]"> {tagsText}</span>}
            </p>
          )}
          <p className="text-[11px] text-neutral-500 mt-1.5 uppercase">Vor 2 Stunden</p>
        </div>
      </div>
    );
  }

  if (platformKey === "facebook") {
    return (
      <div className={`${wrapperBase} bg-white border-neutral-200`} data-testid="mockup-facebook">
        {/* Header */}
        <div className="flex items-start gap-2.5 px-3 pt-3">
          <Avatar size={40} company={company} displayName={displayName} />
          <div className="flex-1 leading-tight">
            <p className="text-[14px] font-semibold text-[#050505]">{handle}</p>
            <p className="text-[11px] text-neutral-500 flex items-center gap-1">
              vor 2 Std · <Globe className="w-2.5 h-2.5" />
            </p>
          </div>
          <MoreHorizontal className="w-5 h-5 text-neutral-700" />
        </div>
        {/* Caption above image (Facebook style) */}
        {caption && (
          <p className="text-[14px] text-[#050505] px-3 py-2.5 leading-snug whitespace-pre-wrap">
            {caption}
            {tagsText && <span className="text-[#1B74E4] block mt-1">{tagsText}</span>}
          </p>
        )}
        {/* Image */}
        <div className={`${PLATFORM_RATIO.facebook} bg-neutral-100 flex items-center justify-center`}>
          {imageUrl ? <img src={imageUrl} alt="" className="w-full h-full object-cover" /> :
            <span className="text-xs text-neutral-400">Kein Bild</span>}
        </div>
        {/* Reactions */}
        <div className="px-3 py-2 flex items-center justify-between text-[12px] text-neutral-500 border-b border-neutral-100">
          <span>👍❤️ Anna und 124 weitere</span>
          <span>23 Kommentare</span>
        </div>
        {/* Action bar */}
        <div className="grid grid-cols-3 px-3 py-1.5 text-[13px] font-medium text-neutral-600">
          <button className="flex items-center justify-center gap-2 py-2 hover:bg-neutral-50 rounded">
            <ThumbsUp className="w-4 h-4" /> Gefaellt mir
          </button>
          <button className="flex items-center justify-center gap-2 py-2 hover:bg-neutral-50 rounded">
            <MessageCircle className="w-4 h-4" /> Kommentar
          </button>
          <button className="flex items-center justify-center gap-2 py-2 hover:bg-neutral-50 rounded">
            <Share2 className="w-4 h-4" /> Teilen
          </button>
        </div>
      </div>
    );
  }

  if (platformKey === "tiktok") {
    return (
      <div className={`${wrapperBase} bg-black border-neutral-800 relative`}
           style={{ maxWidth: "240px" }} data-testid="mockup-tiktok">
        {/* Image fills full canvas */}
        <div className={`${PLATFORM_RATIO.tiktok} relative bg-black`}>
          {imageUrl ? <img src={imageUrl} alt="" className="w-full h-full object-cover" /> :
            <div className="w-full h-full flex items-center justify-center text-xs text-neutral-500">Kein Bild</div>}
          {/* Top bar */}
          <div className="absolute top-2 left-0 right-0 flex justify-center items-center gap-4 text-white text-[12px] font-medium">
            <span className="opacity-60">Folge ich</span>
            <span>|</span>
            <span className="border-b border-white pb-0.5">Fuer dich</span>
          </div>
          {/* Right sidebar icons */}
          <div className="absolute bottom-16 right-2 flex flex-col items-center gap-3.5 text-white">
            <div className="relative">
              <Avatar size={36} company={company} displayName={displayName} />
              <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 bg-[#FE2C55] rounded-full w-4 h-4 flex items-center justify-center">
                <Plus className="w-2.5 h-2.5 text-white" strokeWidth={3} />
              </span>
            </div>
            <div className="flex flex-col items-center">
              <Heart className="w-7 h-7" fill="currentColor" />
              <span className="text-[10px] font-semibold">12.3K</span>
            </div>
            <div className="flex flex-col items-center">
              <MessageCircle className="w-7 h-7" fill="currentColor" />
              <span className="text-[10px] font-semibold">847</span>
            </div>
            <div className="flex flex-col items-center">
              <Repeat2 className="w-7 h-7" />
              <span className="text-[10px] font-semibold">Repost</span>
            </div>
            <div className="flex flex-col items-center">
              <Share2 className="w-7 h-7" fill="currentColor" />
              <span className="text-[10px] font-semibold">Teilen</span>
            </div>
          </div>
          {/* Caption bottom-left */}
          <div className="absolute bottom-2 left-2 right-14 text-white">
            <p className="text-[12px] font-semibold">@{handle}</p>
            <p className="text-[11px] leading-tight mt-0.5 line-clamp-2 whitespace-pre-wrap">{caption}</p>
            {tagsText && <p className="text-[11px] leading-tight mt-0.5 line-clamp-1">{tagsText}</p>}
            <p className="text-[10px] mt-1 flex items-center gap-1 opacity-80">
              <Music className="w-3 h-3" /> Original-Audio · {displayName}
            </p>
          </div>
        </div>
        {/* Bottom nav strip */}
        <div className="bg-black px-3 py-1.5 flex items-center justify-between text-white text-[10px]">
          <span>Startseite</span>
          <span>Entdecken</span>
          <Plus className="w-4 h-4" />
          <span>Inbox</span>
          <span>Profil</span>
        </div>
      </div>
    );
  }

  // LinkedIn
  return (
    <div className={`${wrapperBase} bg-white border-neutral-200`} data-testid="mockup-linkedin">
      <div className="flex items-start gap-2.5 px-3 pt-3">
        <Avatar size={44} company={company} displayName={displayName} />
        <div className="flex-1 leading-tight">
          <p className="text-[14px] font-semibold text-[#000000E6]">{displayName}</p>
          <p className="text-[11px] text-neutral-500">{company.tagline || company.industry || "Unternehmen"}</p>
          <p className="text-[11px] text-neutral-500 flex items-center gap-1">
            vor 2 Std · <Globe className="w-2.5 h-2.5" />
          </p>
        </div>
        <MoreHorizontal className="w-5 h-5 text-neutral-700" />
      </div>
      {(caption || tagsText) && (
        <p className="text-[14px] text-[#000000E6] px-3 py-2.5 leading-snug whitespace-pre-wrap">
          {caption}
          {tagsText && <span className="text-[#0A66C2] block mt-1.5">{tagsText}</span>}
        </p>
      )}
      <div className={`${PLATFORM_RATIO.linkedin} bg-neutral-100 flex items-center justify-center`}>
        {imageUrl ? <img src={imageUrl} alt="" className="w-full h-full object-cover" /> :
          <span className="text-xs text-neutral-400">Kein Bild</span>}
      </div>
      <div className="px-3 py-2 flex items-center justify-between text-[12px] text-neutral-500 border-b border-neutral-100">
        <span>👍🎉💡 87</span>
        <span>14 Kommentare · 5 Reposts</span>
      </div>
      <div className="grid grid-cols-4 px-2 py-1 text-[12px] font-medium text-neutral-600">
        <button className="flex items-center justify-center gap-1 py-2 hover:bg-neutral-50 rounded">
          <ThumbsUp className="w-4 h-4" /> Like
        </button>
        <button className="flex items-center justify-center gap-1 py-2 hover:bg-neutral-50 rounded">
          <MessageCircle className="w-4 h-4" /> Kommentar
        </button>
        <button className="flex items-center justify-center gap-1 py-2 hover:bg-neutral-50 rounded">
          <Repeat2 className="w-4 h-4" /> Repost
        </button>
        <button className="flex items-center justify-center gap-1 py-2 hover:bg-neutral-50 rounded">
          <Send className="w-4 h-4" /> Senden
        </button>
      </div>
    </div>
  );
}
